import { Clear, DragIndicator, ExpandLess, ExpandMore } from '@mui/icons-material';
import { Button, Divider, Grid, IconButton, TextField, useMediaQuery } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { sentence } from 'case';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { SortableElement, SortableHandle } from 'react-sortable-hoc';
import { Card } from '../../../components';
import { ICustomPageSection, IDropdownResponse, SectionType } from '../../../models';
import { AccordionSection } from './AccordionSection';
import { LinkBlockSection } from './LinkBlockSection';
import { MobileAppSection } from './MobileAppSection';
import { SectionTypeSelector } from './SectionTypeSelector';
import { TextBlock } from './TextBlock';
import { YouTubeVideoSection } from './YouTubeVideoSection';
import { HeroSection } from './HeroSection';
import { IFrameSection } from './IframeSection';

const DragHandle = SortableHandle(() => {
  return <DragIndicator />;
});

interface IEditableSection {
  index: number;
  itemIndex: number;
  section: ICustomPageSection;
  setSections: (val: ICustomPageSection[]) => void;
  sections: ICustomPageSection[];
  sectionTypes: IDropdownResponse[];
  customPageId: number;
}

export const EditableSection = SortableElement(
  ({ sectionTypes, sections, section, setSections, itemIndex, customPageId }: IEditableSection) => {
    const { enqueueSnackbar } = useSnackbar();
    // show expanded for new sections added
    const [isCardExpanded, setCardExpanded] = useState<boolean>(
      section?.name === 'Section Title' ? true : false
    );
    const isMobile = useMediaQuery(`(max-width: 767px)`);
    const isTablet = useMediaQuery(`(max-width: 1023px)`);
    const classes = useStyles();
    // use this to deeply copy sections so we don't modify the original array
    const copy: ICustomPageSection[] = JSON.parse(JSON.stringify(sections));

    const [isEditingItem, setEditingItem] = useState<{
      [id: number]: boolean;
    }>({});
    const [customAccordion, setCustomAccordion] = useState<ICustomPageSection[]>([]);

    const addToCopy = (itemIndex: number, val: ICustomPageSection, section: ICustomPageSection) => {
      copy[itemIndex]?.sections?.push({
        ...val,
        name: sentence(val.type),
        sortOrder: section?.sections?.length + 1
      });
    };

    return (
      <div id={`section-${itemIndex}`}>
        <Card
          title={section?.name}
          isExpanded={isCardExpanded}
          action={
            <>
              <IconButton
                onClick={() => setCardExpanded(!isCardExpanded)}
                aria-expanded={isCardExpanded}
                aria-label="show more"
              >
                {isCardExpanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
              <IconButton sx={{ cursor: 'grab' }}>
                <DragHandle />
              </IconButton>
              {isMobile ? (
                <IconButton
                  color="error"
                  onClick={() => {
                    const confirm = window.confirm('Are you sure you want to delete this section?');

                    if (confirm) {
                      setSections(sections.filter(s => s.name !== section.name));
                    }
                  }}
                >
                  <Clear />
                </IconButton>
              ) : (
                <Button
                  color="error"
                  startIcon={<Clear />}
                  onClick={() => {
                    const confirm = window.confirm('Are you sure you want to delete this section?');

                    if (confirm) {
                      setSections(sections.filter(s => s.name !== section.name));
                      enqueueSnackbar('Section removed. Please make sure to save any changes', {
                        variant: 'success'
                      });
                    }
                  }}
                >
                  Remove Section
                </Button>
              )}
            </>
          }
          cardTitleClassName={
            section?.name === 'Section Title' ? classes.cardTitleNew : classes.cardTitle
          }
          headerClassName={section?.name === 'Section Title' ? classes.headerNew : classes.header}
          cardWrapperClassName={classes.cardWrapper}
          cardHeaderActionClassName={classes.cardHeaderAction}
        >
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small"
                label="Name"
                autoComplete="nope"
                variant="outlined"
                value={section.name}
                fullWidth
                onChange={e => {
                  copy[itemIndex].name = e.target.value;
                  setSections(copy);
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small"
                label="Heading"
                autoComplete="nope"
                variant="outlined"
                value={section.text}
                fullWidth
                onChange={e => {
                  copy[itemIndex].text = e.target.value;
                  setSections(copy);
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextBlock
                value={section.subText || ''}
                handleValue={val => {
                  copy[itemIndex].subText = val;
                  setSections(copy);
                }}
                handleRemove={() => {
                  copy[itemIndex].subText = null;
                  setSections(copy);
                }}
              />
            </Grid>
            {section?.sections?.length > 0 && (
              <Grid item xs={12}>
                <Divider />
              </Grid>
            )}
            <Grid item xs={12}>
              <Grid container>
                {section?.sections?.map((subSection, subSectionIndex) => {
                  return (
                    <Grid item xs={12} key={`${subSectionIndex}`} className={classes.section}>
                      <Grid container className={classes.sectionInner}>
                        <Grid item xs={12} sx={{ textAlign: 'right' }}>
                          <Button
                            title={`Remove ${sentence(subSection.type)}`}
                            color="error"
                            startIcon={<Clear />}
                            className={classes.removeSubSectionButton}
                            onClick={() => {
                              const result = window.confirm(
                                'Are you sure you want to remove this section?'
                              );
                              if (result) {
                                const newItems = copy[itemIndex].sections.filter(
                                  s => s.customPageSectionId !== subSection.customPageSectionId
                                );
                                copy[itemIndex].sections = newItems;
                                setSections(copy);
                              }
                            }}
                          >
                            Remove {isTablet ? 'section' : sentence(subSection.type)}
                          </Button>
                        </Grid>
                        <Grid item xs={12}>
                          {subSection.type === SectionType.ACCORDION_SECTION && (
                            <AccordionSection
                              selectedType={subSection.type}
                              customPageSection={customAccordion}
                              setCustomPageSection={setCustomAccordion}
                              isEditing={isEditingItem}
                              setEditing={setEditingItem}
                              parentSections={sections}
                              subSection={subSection}
                              setSections={setSections}
                              parentIndex={itemIndex}
                              index={subSectionIndex}
                            />
                          )}
                          {subSection.type === SectionType.LINK_BLOCK_SECTION && (
                            <LinkBlockSection
                              index={subSectionIndex}
                              parentSections={sections}
                              sections={subSection.sections}
                              parentIndex={itemIndex}
                              setSections={setSections}
                            />
                          )}
                          {subSection.type === SectionType.YOU_TUBE_VIDEO_SECTION && (
                            <YouTubeVideoSection
                              sections={subSection.sections}
                              parentSections={sections}
                              index={subSectionIndex}
                              parentIndex={itemIndex}
                              setSections={setSections}
                            />
                          )}
                          {subSection.type === SectionType.Mobile_App_Section && (
                            <MobileAppSection
                              index={subSectionIndex}
                              parentSections={sections}
                              sections={subSection.sections}
                              parentIndex={itemIndex}
                              setSections={setSections}
                            />
                          )}
                          {subSection.type === SectionType.HERO && (
                            <HeroSection
                              index={subSectionIndex}
                              parentSections={sections}
                              sections={subSection.sections}
                              parentIndex={itemIndex}
                              setSections={setSections}
                            />
                          )}
                          {subSection.type === SectionType.IFRAME && (
                            <IFrameSection
                              index={subSectionIndex}
                              parentSections={sections}
                              sections={subSection.sections}
                              parentIndex={itemIndex}
                              setSections={setSections}
                            />
                          )}
                        </Grid>
                      </Grid>
                    </Grid>
                  );
                })}
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
            <Grid item xs={12} md={8}>
              <SectionTypeSelector
                sectionTypes={sectionTypes}
                handleAddSectionType={val => {
                  addToCopy(itemIndex, val, section);
                  setSections(copy);
                }}
              />
            </Grid>
          </Grid>
        </Card>
      </div>
    );
  }
);

const useStyles = makeStyles<Theme>(theme => ({
  cardTitle: {
    textTransform: 'none',
    fontSize: '1.25rem'
  },
  cardTitleNew: {
    textTransform: 'none',
    fontSize: '1.25rem',
    fontStyle: 'italic'
  },
  cardWrapper: {
    flex: 1,
    height: '100%'
  },
  header: {
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.common.white,
    borderBottom: `1px solid ${theme.palette.grey[200]}`
  },
  headerNew: {
    color: theme.palette.grey[400],
    backgroundColor: theme.palette.common.white,
    borderBottom: `1px solid ${theme.palette.grey[200]}`
  },
  removeSubSectionButton: {
    top: 0,
    right: 0,
    [theme.breakpoints.up('sm')]: {
      position: 'absolute'
    }
  },
  section: {
    borderTop: `1px solid ${theme.palette.grey[200]}`,
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1.25),
    '&:first-child': {
      borderTop: 'none',
      paddingTop: 0
    }
  },
  sectionInner: {
    position: 'relative'
  }
}));
